import React, { useEffect } from 'react';
import {
  Form,
  FormInstance,
  DateSerialized,
  SelectBooleanInput,
  DateInput
} from '@lightbeamhealth/blueprint-form';
import { Row, Col } from '../../../../grid';
import { EndDate } from '../date';
import SelectMethod from '../method';
import SelectLanguage from '../language';
import SelectTime from '../time';
import Weekday from '../weekday';
import Timezone from '../timezone';
import { CardBlock } from '../../card';
import {
  GetCurrentTZ,
  GetFormalDate,
  ShorthandDate
} from '../../../../libs/time';
import { useScheduleForm } from '../../../../contexts/form';

interface EpxBreastFeedData {
  method: string;
  start: string;
  end?: string;
  tz: string;
  times: string[];
  days: number[];
  step?: number;
  isBorn?: boolean;
  includeEducation?: boolean;
  expectedBirthDate?: string;
  birthDate?: string;
}

type EpxBreastFeedDataRaw = DateSerialized<
  EpxBreastFeedData,
  'start' | 'end' | 'expectedBirthDate' | 'birthDate'
>;

export const EpxBreastFeedDefaultData: EpxBreastFeedData = {
  method: 'message',
  start: GetFormalDate(ShorthandDate.Today),
  end: GetFormalDate(ShorthandDate.ThreeMonths),
  tz: GetCurrentTZ(),
  times: ['10:00'],
  days: [1, 4],
  isBorn: false,
  includeEducation: true,
  expectedBirthDate: GetFormalDate(ShorthandDate.Today),
  birthDate: null
};

const DATE_FORMAT = 'YYYY-MM-DD';
const { DeserializeDate, SerializeDate } = FormInstance;

interface EpxBreastFeedFormProps {
  setForm?: (form: FormInstance<EpxBreastFeedDataRaw>) => void;
  onSubmit?: (data: EpxBreastFeedData) => void;
  isDisabled?: boolean;
}

const EpxBreastFeedForm = ({
  setForm,
  onSubmit,
  isDisabled
}: EpxBreastFeedFormProps): JSX.Element => {
  const { isEditMode } = useScheduleForm();
  const serialize = (data: EpxBreastFeedDataRaw): EpxBreastFeedData => {
    return {
      ...data,
      start: SerializeDate(data.start, DATE_FORMAT),
      end: SerializeDate(data.end, DATE_FORMAT),
      times: data.times.slice(0, 1),
      expectedBirthDate: SerializeDate(data.start, DATE_FORMAT),
      birthDate: data.isBorn ? SerializeDate(data.start, DATE_FORMAT) : null
    };
  };
  const deserialize = (data: EpxBreastFeedData): EpxBreastFeedDataRaw => {
    return {
      ...data,
      method: data.method || 'message',
      start: DeserializeDate(data.start, DATE_FORMAT),
      end: DeserializeDate(data.end, DATE_FORMAT),
      expectedBirthDate: DeserializeDate(data.start, DATE_FORMAT),
      birthDate: data.isBorn ? DeserializeDate(data.start, DATE_FORMAT) : null
    };
  };
  const defaultData = deserialize(EpxBreastFeedDefaultData);
  const form = new FormInstance<EpxBreastFeedDataRaw, EpxBreastFeedData>(
    defaultData,
    { serialize, deserialize }
  );
  useEffect(() => {
    if (setForm) {
      setForm(form);
    }
  }, []);
  return (
    <CardBlock bare>
      <Form form={form} onSubmit={onSubmit} disabled={isDisabled}>
        {(props) => {
          return (
            <>
              <CardBlock>
                <Row>
                  <Col sm={12}>
                    <SelectMethod isMessageOnly />
                  </Col>
                  <Col sm={12}>
                    <SelectLanguage />
                  </Col>
                </Row>
              </CardBlock>
              <CardBlock>
                <Row>
                  <Col sm={12}>
                    <DateInput
                      label={
                        props.values.isBorn
                          ? "Baby's Birth Date"
                          : "Baby's Expected Birth Date"
                      }
                      name="start"
                      fill
                      disabled={isEditMode}
                    />
                  </Col>
                  <Col sm={12}>
                    <SelectBooleanInput
                      label="Baby is Born?"
                      name="isBorn"
                      trueLabel="Yes"
                      falseLabel="No"
                      fill
                      disabled={
                        isEditMode &&
                        props.values.expectedBirthDate < new Date()
                      }
                    />
                  </Col>
                  <Col sm={12}>
                    <EndDate label="End Messages on" name="end" />
                  </Col>
                  <Col sm={12}>
                    <Timezone label="Timezone" name="tz" fill />
                  </Col>
                  <Col sm={12}>
                    <SelectTime label="Message At" name="times[0]" />
                  </Col>
                </Row>
                <Row>
                  <Col sm={24}>
                    <Weekday name="days" numItems={2} inline />
                  </Col>
                </Row>
              </CardBlock>
              <CardBlock>
                <Row>
                  <Col sm={12}>
                    <SelectBooleanInput
                      label="Education & Motivation Messages"
                      name="includeEducation"
                      trueLabel="On"
                      falseLabel="Off"
                      fill
                    />
                  </Col>
                </Row>
              </CardBlock>
            </>
          );
        }}
      </Form>
    </CardBlock>
  );
};

export default EpxBreastFeedForm;
